import Cookies from 'universal-cookie';
import moment from 'moment';
import { getAvailablePremiumLookupKeysFromCookie } from '../functions/pricing-offer';
import { getAvailablePremiumLookupKey } from '../functions/billing-period';
import { routes } from '../../config/routes';
import { LookupKeyEnum } from '../../defs/api';
import { ImportResultWarning } from '../stores/trade-import-dialog-store';

export class CookieService {
    cookie?: string;

    jwtKey = 'jwt';

    jwtImpersonateKey = 'jwt_impersonate';

    privateModeEnabledKey = 'private_mode_enabled';

    premiumPlusDialogOpenProtectionKey = 'premium_plus_dialog_open_protection';

    bannerYearlyOpenProtectionKey = 'yearly_banner_open_protection';

    bannerInvestownClosedKey = 'banner_investown_closed';

    interestedInSubscriptionKey = 'interested_in_subscription';

    preferredSubscriptionKey = 'preferred_subscription';

    availablePremiumLookupKeys: readonly string[];

    calculateFeesKey = 'calculate_fees';

    agreedTermsKey = 'agreed_terms';

    agreedMarketingKey = 'agreed_marketing';

    importResultMessage = 'import_result_message';

    importResultWarnings = 'import_result_warnings';

    importNotFoundSymbols = 'import_not_found_symbols';

    predepissimoneryPromoKey = 'predepissimonery_promo';

    signupCloseRedirectUrlKey = 'signup_close_redirect_url';

    constructor(cookie?: string, availablePremiumLookupKeys?: readonly string[]) {
        this.cookie = cookie;
        this.availablePremiumLookupKeys = availablePremiumLookupKeys ?? getAvailablePremiumLookupKeysFromCookie(cookie);
    }

    getSignupLookupKey(lookupKey?: LookupKeyEnum | null): LookupKeyEnum {
        return getAvailablePremiumLookupKey(
            lookupKey ?? this.getInterestedInSubscription() ?? this.getPreferredSubscription(),
            this.availablePremiumLookupKeys,
        );
    }

    getPreferredSubscription(): LookupKeyEnum | null {
        return new Cookies(this.cookie).get(this.preferredSubscriptionKey) ?? null;
    }

    setPreferredSubscription(lookupKey: LookupKeyEnum): void {
        new Cookies(this.cookie).set(this.preferredSubscriptionKey, lookupKey, { path: '/' });
    }

    selectPreferredSubscription(lookupKey: LookupKeyEnum): void {
        this.setPreferredSubscription(lookupKey);
        this.setInterestedInSubscription(null);
    }

    isPrivateModeEnabled(): boolean {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.privateModeEnabledKey) === 'true';
    }

    togglePrivateModeEnabled(): void {
        const cookies = new Cookies(this.cookie);
        if (this.isPrivateModeEnabled()) {
            cookies.remove(this.privateModeEnabledKey, { path: '/' });
        } else {
            cookies.set(this.privateModeEnabledKey, 'true', {
                path: '/',
                expires: moment().add(1, 'year').toDate(),
            });
        }
    }

    setRedirectUrl(redirect: string) {
        if (redirect !== routes.app.index) {
            window.localStorage.setItem('redirectUrl', redirect);
        }
    }

    setImportResultMessage(message: string) {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.importResultMessage, message, { path: '/' });
    }

    removeImportResultMessage() {
        const cookies = new Cookies(this.cookie);
        cookies.remove(this.importResultMessage, { path: '/' });
    }

    getImportResultMessage(): string | null {
        const cookies = new Cookies(this.cookie);
        const lastImportMessage = cookies.get(this.importResultMessage);
        return lastImportMessage || null;
    }

    setImportResultWarnings(warnings: ImportResultWarning[]) {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.importResultWarnings, warnings, { path: '/' });
    }

    removeImportResultWarnings() {
        const cookies = new Cookies(this.cookie);
        cookies.remove(this.importResultWarnings, { path: '/' });
    }

    getImportResultWarnings(): ImportResultWarning[] {
        const cookies = new Cookies(this.cookie);
        const lastImportWarnings = cookies.get(this.importResultWarnings);
        return lastImportWarnings || null;
    }

    setImportNotFoundSymbols(symbols: string[]) {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.importNotFoundSymbols, symbols, { path: '/' });
    }

    removeImportNotFoundSymbols() {
        const cookies = new Cookies(this.cookie);
        cookies.remove(this.importNotFoundSymbols, { path: '/' });
    }

    getImportNotFoundSymbols(): string[] | null {
        const cookies = new Cookies(this.cookie);
        const notFoundSymbols = cookies.get(this.importNotFoundSymbols);
        return notFoundSymbols || null;
    }

    setPredepissimoneryPromo(enabled: boolean): void {
        const cookies = new Cookies(this.cookie);

        if (enabled) {
            cookies.set(this.predepissimoneryPromoKey, 'true', {
                path: '/',
                expires: moment().add(30, 'day').toDate(),
            });
        } else {
            cookies.remove(this.predepissimoneryPromoKey, { path: '/' });
        }
    }

    hasPredepissimoneryPromo(): boolean {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.predepissimoneryPromoKey) === 'true';
    }

    getRedirectUrl() {
        return window.localStorage.getItem('redirectUrl');
    }

    clearRedirectUrl() {
        window.localStorage.removeItem('redirectUrl');
    }

    loadRedirectUrl() {
        const redirectUrl = window.localStorage.getItem('redirectUrl');
        if (redirectUrl) {
            window.location.replace(redirectUrl);
            this.clearRedirectUrl();
        }
    }

    hasJwtImpersonateToken(): boolean {
        return this.getJwtImpersonateToken() !== undefined;
    }

    getJwtImpersonateToken(): string | undefined {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.jwtImpersonateKey);
    }

    hasJwtToken(): boolean {
        return this.getJwtToken() !== undefined;
    }

    getJwtToken(): string | undefined {
        const cookies = new Cookies(this.cookie);

        const impersonationToken = this.getJwtImpersonateToken();

        return impersonationToken || cookies.get(this.jwtKey);
    }

    setJwtToken(accessToken: string | null, rememberMe?: boolean): void {
        const cookies = new Cookies(this.cookie);

        if (accessToken) {
            cookies.set(this.jwtKey, accessToken, {
                path: '/',
                expires: rememberMe ? moment().add(1, 'year').toDate() : undefined,
            });
        } else {
            cookies.remove(this.jwtKey, { path: '/' });
            cookies.remove(this.jwtKey, { domain: process.env.NEXT_PUBLIC_API_COOKIE_DOMAIN });
        }
    }

    setImpersonateJwtToken(accessToken: string | null): void {
        const cookies = new Cookies(this.cookie);

        if (accessToken) {
            cookies.set(this.jwtImpersonateKey, accessToken, { path: '/' });
        } else {
            cookies.remove(this.jwtImpersonateKey, { path: '/' });
        }
    }

    setPremiumPlusDialogOpenProtection(seconds: number): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.premiumPlusDialogOpenProtectionKey, 'true', { path: '/', maxAge: seconds });
    }

    hasPremiumPlusDialogOpenProtection(): boolean {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.premiumPlusDialogOpenProtectionKey) === 'true';
    }

    setBannerYearlyOpenProtection(seconds = 60 * 60 * 24 * 30): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.bannerYearlyOpenProtectionKey, 'true', {
            path: '/',
            maxAge: seconds,
        });
    }

    setInvestownBannerClosed(): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.bannerInvestownClosedKey, 'true', {
            path: '/',
            // Nastavujeme platnost na 10 let pro prakticky trvalé skrytí
            expires: moment().add(10, 'year').toDate(),
        });
    }

    hasInvestownBannerClosed(): boolean {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.bannerInvestownClosedKey) === 'true';
    }

    hasBannerYearlyOpenProtection(): boolean {
        const cookies = new Cookies(this.cookie);
        return cookies.get(this.bannerYearlyOpenProtectionKey) === 'true';
    }

    setInterestedInSubscription(subscription: LookupKeyEnum | null): void {
        const cookies = new Cookies(this.cookie);

        if (subscription) {
            this.setPreferredSubscription(subscription);
            cookies.set(this.interestedInSubscriptionKey, subscription, { path: '/' });
        } else {
            cookies.remove(this.interestedInSubscriptionKey, { path: '/' });
            const domain = process.env.NEXT_PUBLIC_API_COOKIE_DOMAIN;
            if (domain && domain !== 'localhost') {
                cookies.remove(this.interestedInSubscriptionKey, { path: '/', domain });
            }
        }
    }

    getInterestedInSubscription(): LookupKeyEnum | null {
        const cookies = new Cookies(this.cookie);
        const interestedInSubscription = cookies.get(this.interestedInSubscriptionKey);

        if (!interestedInSubscription) {
            return null;
        }

        return interestedInSubscription as LookupKeyEnum;
    }

    setSignupCloseRedirectUrl(url: string | null): void {
        const cookies = new Cookies(this.cookie);

        if (url) {
            cookies.set(this.signupCloseRedirectUrlKey, url, {
                path: '/',
                maxAge: 60 * 30,
            });
        } else {
            cookies.remove(this.signupCloseRedirectUrlKey, { path: '/' });
        }
    }

    getSignupCloseRedirectUrl(): string | null {
        const cookies = new Cookies(this.cookie);
        const redirectUrl = cookies.get(this.signupCloseRedirectUrlKey);

        return redirectUrl || null;
    }

    setCalculateFees(calculateFees: boolean): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.calculateFeesKey, calculateFees, { path: '/' });
    }

    getCalculateFees(): boolean {
        const cookies = new Cookies(this.cookie);
        const calculateFees = cookies.get(this.calculateFeesKey);
        return calculateFees === 'true';
    }

    setAgreedTerms(agreedTerms: boolean): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.agreedTermsKey, agreedTerms, { path: '/' });
    }

    setAgreedMarketing(agreedMarketing: boolean): void {
        const cookies = new Cookies(this.cookie);
        cookies.set(this.agreedMarketingKey, agreedMarketing, { path: '/' });
    }
}
